import styled from '@emotion/styled'
import { colors } from '../../..'
import { Timestamp } from '../../../content/CardTimestamp/CardTimestamp'
import { breakpoint, calcRem, themedValue } from '../../../__styling'

export const StyledMilestonesContainer = styled('div')(({ theme }) => ({
    backgroundColor: colors.thewest.greyErmine,
    margin: themedValue(theme, {
        sevennews: calcRem(theme.margins.lg, -7, 0),
        fallback: calcRem(0, theme.margins.md * -1),
    }),
}))

interface StyledMilestonesTimelineProps {
    restrictedHeight: boolean
}

export const StyledMilestonesTimeline = styled(
    'div',
)<StyledMilestonesTimelineProps>(({ theme, restrictedHeight }) => ({
    position: 'relative',
    overflow: 'hidden',
    maxHeight: restrictedHeight ? '500px' : '',

    [breakpoint('sm')]: {
        maxHeight: restrictedHeight ? '400px' : '',
    },

    [breakpoint('md')]: {
        maxHeight: restrictedHeight ? '350px' : '',
    },

    '& > a': {
        textDecoration: 'none',
    },

    '&::after': {
        display: restrictedHeight ? 'block' : 'none',
        position: 'absolute',
        height: '150px',
        width: '100%',
        content: `''`,
        bottom: '-12px',
        background: `linear-gradient(to bottom,
               rgba(244, 245, 245, 0) 0%,
               rgba(244, 245, 245, 1) 80%
            )`,
        pointerEvents: 'none',
        zIndex: 12,
    },
}))

export const StyledMilestone = styled('div')<{ dimmed: boolean }>(
    ({ theme, dimmed }) => ({
        position: 'relative',
        margin: calcRem(0, 20),
        padding: calcRem(0, 14, theme.margins.md),
        borderLeft: `1px solid ${colors.thewest.greySeal}`,
        opacity: dimmed ? 0.2 : 1,
        '&::after': {
            display: 'block',
            position: 'absolute',
            borderRadius: '50%',
            content: `''`,
            left: -5,
            top: 3,
            width: 10,
            height: 10,
            backgroundColor: theme.colors.brand,
            zIndex: 11,
        },
        '&:hover': {
            opacity: 1,
        },
    }),
)

export const StyledMilestoneTimestamp = styled(Timestamp)(({ theme }) => ({
    marginLeft: 0,
    backgroundColor: 'transparent',
    color: theme.colors.palette.black,
    fontWeight: themedValue(theme, {
        sevennews: 800,
        fallback: 700,
    }),
    fontSize: calcRem(15),
    lineHeight: calcRem(16),
    fontFamily: themedValue(theme, {
        sevennews: theme.fonts.headingAlternative,
        fallback: theme.fonts.sansSerif,
    }),
}))

export const StyledMilestoneTitle = styled('span')(({ theme }) => ({
    textDecoration: 'none',
    color: theme.colors.palette.black,
    fontWeight: themedValue(theme, {
        sevennews: 500,
        fallback: 'inherit',
    }),
    fontFamily: themedValue(theme, {
        sevennews: theme.fonts.headingAlternative,
        fallback: theme.fonts.serif,
    }),
}))

export const StyledMilestonesTitle = styled('h3')(({ theme }) => ({
    fontSize: calcRem(22),
    padding: calcRem(theme.margins.md),
    margin: 0,
}))

export const StyledShowMoreButton = styled('button')<{ isVisible?: boolean }>(
    ({ theme, isVisible }) => ({
        display: isVisible ? 'flex' : 'none',
        position: 'relative',
        bottom: 25,
        height: calcRem(32),
        minWidth: calcRem(100),
        marginTop: theme.margins.lg,
        marginLeft: theme.margins.md,
        cursor: 'pointer',
        border: `1px solid ${theme.colors.brand}`,
        borderRadius: calcRem(2),

        zIndex: 13,
        alignItems: 'center',
        justifyContent: 'center',

        color: theme.colors.brand,
        fontFamily: theme.fonts.sansSerif,
        fontSize: calcRem(13),
        fontWeight: 700,

        /* Increase size of +/- symbol  */
        '& > span': {
            fontSize: calcRem(16),
            marginRight: calcRem(3),
        },

        '&::after': {
            display: 'none',
            border: `1px solid ${theme.colors.palette.black}`,
            content: `''`,
            position: 'absolute',
            top: -3,
            left: -3,
            width: 'calc(100% + 6px)',
            height: 'calc(100% + 6px)',
            borderRadius: calcRem(4),
        },

        '&:hover': {
            backgroundColor: `rgba(4, 61, 226, 0.04)`,
        },

        '&:focus': {
            outline: 'none',

            '&::after': {
                display: 'block',
            },
        },
    }),
)
